import { useOrders, useUserInfo, useWarehouseAddress } from "@kupikupi/api";
import { customerCode } from "@kupikupi/brand";
import { formatPrice } from "@kupikupi/money";
import { Link, useNavigate } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import { useAuthToken } from "../lib/auth";

export function TransportPage() {
  const { t } = useTranslation("transport");
  const authToken = useAuthToken();
  const navigate = useNavigate();

  const { data, isLoading } = useOrders(authToken, {
    pageNo: 1,
    pageSize: 20,
    hideCancelOrder: 1,
  });
  const records = data?.records ?? [];

  // Warehouse address from API (real, not env vars)
  const { data: warehouseAddr } = useWarehouseAddress();

  // User ID for the customer code (KKxxxxxxx)
  const { data: userInfo } = useUserInfo(authToken);
  const code = customerCode(userInfo?.id ?? null);

  const inTransit = records.filter((r) => r.status === "SHIPPED").length;
  const pendingSupplement = records.filter((r) => r.status === "WAIT_PAYMENT").length;
  const delivered = records.filter((r) => r.status === "COMPLETED").length;

  // Build warehouse copy text once we have real data
  const warehouseCopy = warehouseAddr
    ? [
        warehouseAddr.recipientName ? `${warehouseAddr.recipientName}(${code})` : "",
        warehouseAddr.mobile,
        warehouseAddr.detail,
        warehouseAddr.postalCode,
      ]
        .filter(Boolean)
        .join("\n")
    : "";

  const warehouseConfigured = Boolean(warehouseAddr?.recipientName || warehouseAddr?.detail);

  return (
    <TgPage tabbar={false}>
      <TgHeader
        title={t("head.title")}
        back
        right={
          <button
            type="button"
            className="tg-header__r"
            onClick={() => navigate({ to: "/submit-parcel" as never })}
            style={{ fontSize: 12, fontWeight: 700 }}
          >
            {t("card.create_short")}
          </button>
        }
      />

      <div style={{ background: "var(--gradient-sky)", padding: "18px 16px", textAlign: "center" }}>
        <span
          style={{
            display: "inline-block",
            padding: "3px 10px",
            background: "rgba(255,255,255,.7)",
            color: "var(--color-primary-fill)",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          {t("head.badge")}
        </span>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 20,
            fontWeight: 900,
            margin: "8px 0 4px",
          }}
        >
          {t("head.hero_line1")}{" "}
          <span style={{ color: "var(--color-cta)" }}>{t("head.hero_accent")}</span>
        </h1>
        <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: 0 }}>
          {t("head.hero_sub")}
        </p>
      </div>

      <div className="tg-body" style={{ flex: 1 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6 }}>
          <Kpi
            bg="var(--color-primary-soft)"
            fg="var(--color-primary-fill)"
            v={String(inTransit)}
            label={t("kpi.transit")}
          />
          <Kpi
            bg="var(--color-cta-soft)"
            fg="var(--color-cta)"
            v={String(pendingSupplement)}
            label={t("kpi.supplement")}
          />
          <Kpi
            bg="var(--color-surface)"
            fg="var(--color-text)"
            v={String(delivered)}
            label={t("kpi.delivered")}
          />
        </div>

        {isLoading && (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: "var(--color-text-muted)",
              fontSize: 12,
            }}
          >
            …
          </div>
        )}

        {!isLoading && records.length === 0 && (
          <TgEmpty icon="📍" title={t("empty.title")} hint={t("empty.hint")} />
        )}

        {records.map((o) => {
          const isSupplement = o.status === "WAIT_PAYMENT";
          const isTransit = o.status === "SHIPPED";
          const isStored = o.status === "STORED";

          // Badge class / style
          let badgeClass = "tg-st";
          let badgeStyle: CSSProperties | undefined;
          let dotStyle: CSSProperties | undefined;

          if (isSupplement) {
            badgeStyle = {
              background: "var(--color-warning-soft)",
              color: "var(--color-warning-foreground)",
              border: "1px solid rgba(255,162,0,.3)",
            };
            dotStyle = { background: "var(--color-warning)" };
          } else if (isTransit) {
            badgeClass = "tg-st tg-st--transit";
          } else if (isStored) {
            badgeStyle = {
              background: "var(--color-warning-soft)",
              color: "var(--color-warning-foreground)",
              border: "1px solid rgba(255,162,0,.3)",
            };
            dotStyle = { background: "var(--color-warning)" };
          }

          const badgeLabel = isSupplement
            ? t("card.supplement_badge")
            : isTransit
              ? t("card.transit_badge")
              : isStored
                ? t("card.stored_badge")
                : o.statusName;

          // Supplement: show actual express fee from API, hide est/actual weight (no data in OrderSummary)
          const supplementFee = isSupplement && o.expressFeeRubKopecks > 0
            ? formatPrice(o.expressFeeRubKopecks, "ru-RU", "RUB")
            : null;

          return (
            <Link
              key={o.orderNo}
              to={"/order-detail/$orderNo" as never}
              params={{ orderNo: o.orderNo } as never}
              style={{
                background: "#fff",
                border: isSupplement || isStored
                  ? "1.5px solid var(--color-warning)"
                  : "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "12px 14px",
                textDecoration: "none",
                color: "inherit",
                display: "block",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 6,
                }}
              >
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700 }}>
                  TR-{o.orderNo.slice(-4)}
                </span>
                <span className={badgeClass} style={badgeStyle}>
                  <span className="tg-st__dot" style={dotStyle} />
                  {badgeLabel}
                </span>
              </div>
              <strong style={{ fontSize: 12 }}>{o.items?.[0]?.title ?? o.orderNo}</strong>

              {/* WAIT_PAYMENT: show supplement fee from real expressFeeRubKopecks; hide est/actual weight (no data) */}
              {isSupplement && supplementFee && (
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3 }}>
                  {t("card.supplement_fee", { amount: supplementFee })}
                </div>
              )}

              {/* SHIPPED: design shows weight·carrier·ETA — these fields are not in OrderSummary; section hidden */}

              {/* STORED: design shows forecast kg · waiting for seller — forecast weight not in OrderSummary; show waiting text only */}
              {isStored && (
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3 }}>
                  {t("card.stored_detail")}
                </div>
              )}
            </Link>
          );
        })}

        {/* Warehouse address — real data from useWarehouseAddress, not env vars */}
        <div
          className="tg-card"
          style={{
            background: "var(--color-surface)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            lineHeight: 1.7,
          }}
        >
          <strong
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 12,
              display: "block",
              marginBottom: 6,
            }}
          >
            {t("warehouse_addr.title")}
          </strong>
          {warehouseConfigured ? (
            <>
              {warehouseAddr?.recipientName}(
              <strong style={{ color: "var(--color-cta)" }}>{code}</strong>)
              <br />
              {warehouseAddr?.mobile}
              <br />
              {warehouseAddr?.detail}
              {warehouseAddr?.postalCode ? (
                <>
                  <br />
                  {warehouseAddr.postalCode}
                </>
              ) : null}
              <button
                type="button"
                style={{
                  marginTop: 6,
                  background: "var(--color-text)",
                  color: "#fff",
                  border: 0,
                  padding: "5px 12px",
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 800,
                  display: "block",
                  cursor: "pointer",
                }}
                onClick={() => {
                  navigator.clipboard?.writeText(warehouseCopy);
                }}
              >
                {t("warehouse_addr.copy")}
              </button>
            </>
          ) : (
            t("warehouse_addr.coming_soon")
          )}
        </div>
      </div>

      <TgMain onClick={() => navigate({ to: "/submit-parcel" as never })}>
        {t("card.create")}
      </TgMain>
    </TgPage>
  );
}

function Kpi({ bg, fg, v, label }: { bg: string; fg: string; v: string; label: string }) {
  return (
    <div style={{ background: bg, borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 900, color: fg }}>
        {v}
      </div>
      <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>{label}</div>
    </div>
  );
}
